import { convertPrices } from '../utils/storefront'
import { sendKlevuAnalytics, convertKlevuProductData } from '../utils/klevu'
import { formatMoney } from '../utils/shopify'

export default (reducedFilters = '', limit = 16) => ({
    collectionName: Alpine.$persist('')
        .as('collection-collectionName')
        .using(sessionStorage),
    products: Alpine.$persist([])
        .as('collection-products')
        .using(sessionStorage),
    gridItems: Alpine.$persist([])
        .as('collection-gridItems')
        .using(sessionStorage),
    totalProductsFound: Alpine.$persist(0)
        .as('collection-totalProductsFound')
        .using(sessionStorage),
    limit: limit,
    offset: Alpine.$persist(0).as('collection-offset').using(sessionStorage),
    filters: Alpine.$persist([]).as('collection-filters').using(sessionStorage),
    filtersOpen: false,
    activeFilters: Alpine.$persist([])
        .as('collection-activeFilters')
        .using(sessionStorage),
    minPriceLimit: Alpine.$persist(null)
        .as('collection-minPriceLimit')
        .using(sessionStorage),
    minPrice: Alpine.$persist(null)
        .as('collection-minPrice')
        .using(sessionStorage),
    minPriceLabel: Alpine.$persist('')
        .as('collection-minPriceLabel')
        .using(sessionStorage),
    maxPriceLimit: Alpine.$persist(null)
        .as('collection-maxPriceLimit')
        .using(sessionStorage),
    maxPrice: Alpine.$persist(null)
        .as('collection-maxPrice')
        .using(sessionStorage),
    maxPriceLabel: Alpine.$persist('')
        .as('collection-maxPriceLabel')
        .using(sessionStorage),
    minThumb: Alpine.$persist(0)
        .as('collection-minThumb')
        .using(sessionStorage),
    maxThumb: Alpine.$persist(0)
        .as('collection-maxThumb')
        .using(sessionStorage),
    buffer: Alpine.$persist(0).as('collection-buffer').using(sessionStorage),
    sortOptions: [
        {
            label: 'Most Relevant',
            value: 'RELEVANCE',
        },
        {
            label: 'Most Recent',
            value: 'NEW_ARRIVAL_DESC',
        },
        {
            label: 'Price: Low to High',
            value: 'PRICE_ASC',
        },
        {
            label: 'Price: High to Low',
            value: 'PRICE_DESC',
        },
    ],
    activeSort: Alpine.$persist('RELEVANCE')
        .as('collection-activeSort')
        .using(sessionStorage),
    activeSortLabel: Alpine.$persist('Most Relevant')
        .as('collection-activeSortLabel')
        .using(sessionStorage),
    loading: true,
    loadMoreLoading: false,
    loadMoreText: 'Load More',
    currency: {
        ...shopifyData.currency,
        code: Alpine.$persist('')
            .as('collection-currencyCode')
            .using(sessionStorage),
    },
    columnSize: Alpine.$persist(2)
        .as('collection-columnSize')
        .using(sessionStorage),
    ctas: [],
    initialApplyFilters: null,
    urlCandidateParams: null,
    suppressFilterWatch: false,

    init() {
        this.ctas = window.shopifyData.collection.ctas

        if (!this.currency.code) this.currency.code = this.currency.iso_code
        
        // Parse all URL params into candidate filter params up-front so we can
        // know if URL-supplied filters exist and should take precedence over
        // persisted state.
        const url = new URL(window.location)
        const candidates = this.parseUrlCandidates(url)

        const hasUrlCandidates = Object.keys(candidates).length > 0

        // Determine whether applying the URL candidates would actually
        // change the currently persisted filters. If it would not change
        // anything, we can skip the two-step fetch/apply flow to avoid a
        // redundant network request.
        const candidatesHaveChanged = hasUrlCandidates && this.wouldUrlCandidatesChangeFilters(candidates)

        if (
            this.collectionName !== shopifyData.collection.title ||
            this.currency.code !== shopifyData.currency.iso_code ||
            (hasUrlCandidates && candidatesHaveChanged)
        ) {
            // If switching collections, or currency changed, or URL includes
            // filter params, reset persisted state so URL params take effect.
            this.resetData()
            this.collectionName = shopifyData.collection.title
            this.currency.code = shopifyData.currency.iso_code

            // Only retain URL candidates if they will change the current
            // selection or if we've just changed collection/currency.
            this.urlCandidateParams = hasUrlCandidates ? candidates : null

            const filters = reducedFilters ? reducedFilters.split(',') : []
            // Initial request: ask for filter metadata but do not apply any filters yet.
            const request = this.createRequest(true, filters, null)
            this.$nextTick(() => this.fetchProducts(request, 'loading', true))
        } else {
            this.loading = false
            this.updateUrlWithFilters()
        }

        this.$watch(
            'loadMoreLoading',
            (value) => (this.loadMoreText = value ? 'Loading…' : 'Load more')
        )

        this.$watch('filters', (value, oldValue) => {
            if (this.suppressFilterWatch) return

            if (oldValue.length) {
                this.updateActiveFilters()
                this.fetchFilteredSortedProducts()
            }
        })

        // Update the URL to add/delete each filter and it's selected values
        this.$watch('activeFilters', (value) => {
            this.updateUrlWithFilters()
        })

        this.$watch('activeSort', (value) => {
            this.setActiveSortLabel(value)
            this.fetchFilteredSortedProducts()
        })

        this.$nextTick(() => {
            const apiKey = shopifyData.klevuApi.key
            const analyticsUrl = [
                `https://stats.ksearchnet.com/analytics/categoryProductViewTracking`,
                `?klevu_apiKey=${apiKey}`,
                `&klevu_categoryName=${encodeURIComponent(this.collectionName)}`,
                `&klevu_categoryPath=${encodeURIComponent(this.collectionName)}`,
                `&klevu_productIds=${this.products.map((product) => product.id).join(',')}`,
            ].join('')

            fetch(analyticsUrl, {
                method: 'GET',
                headers: {},
            })
        })

        const sort = document.getElementById('results-sort')
        if (sort) {
            window.addEventListener('load', () => {
                this.updateSelectWidth(sort)
            })
        }
    },

    parseUrlCandidates(url) {
        const candidates = {}
        const ESC_TOKEN = '::COMMA::'

        for (const [key, value] of url.searchParams.entries()) {
            if (!key) continue
            const parts = value && value.includes(',') ? value.split(',') : [value]
            const unescaped = parts.map((p) => p.replace(new RegExp(ESC_TOKEN, 'g'), ','))
            candidates[key] = candidates[key] || []
            candidates[key].push(...unescaped)
        }

        return candidates
    },
    // Return true if applying the given URL candidate params would change
    // the currently selected (persisted) filters.
    wouldUrlCandidatesChangeFilters(candidates) {
        if (!candidates) return false

        // Build a lookup of current selected values by filter key
        const selectedMap = (this.filters || []).reduce((acc, filter) => {
            const selected = filter.options
                .filter((o) => o.selected)
                .map((o) => o.value)
            acc[filter.key] = new Set(selected)
            return acc
        }, {})

        return Object.keys(candidates).some((key) => {
            const vals = candidates[key] || []
            const set = selectedMap[key]
            // If there is no persisted filter for this key, applying will change
            if (!set) return true
            // If any candidate value is not already selected, applying will change
            return vals.some((v) => !set.has(v))
        })
    },
    escapeForParam(value) {
        const ESC_TOKEN = '::COMMA::'
        return (value || '').replace(/,/g, ESC_TOKEN)
    },
    applyUrlCandidatesToFilters() {
        if (!this.urlCandidateParams) return

        this.suppressFilterWatch = true

        Object.keys(this.urlCandidateParams).forEach((key) => {
            const values = this.urlCandidateParams[key]
            const f = this.filters.find((filter) => filter.key === key)
            if (!f) return

            f.options.forEach((option) => {
                if (values.includes(option.value)) {
                    option.selected = true
                }
            })
        })

        this.updateActiveFilters()

        // Clear candidates so we don't reapply on subsequent fetches
        this.urlCandidateParams = null
        this.suppressFilterWatch = false
    },
    updateUrlWithFilters() {
        const url = new URL(window.location)

        const map = this.activeFilters.reduce((acc, filter) => {
            acc[filter.filterKey] = acc[filter.filterKey] || []
            acc[filter.filterKey].push(filter.value)
            return acc
        }, {})

        // Remove any existing filter keys so removed filters are fully cleared
        const existingFilterKeys = (this.filters || []).map((f) => f.key)
        existingFilterKeys.forEach((k) => url.searchParams.delete(k))

        // Set current active filters from the map
        Object.keys(map).forEach((key) => {
            const escaped = map[key].map((v) => this.escapeForParam(v))
            url.searchParams.set(key, escaped.join(','))
        })

        window.history.replaceState({}, '', url)
    },
    resetData() {
        this.filters = []
        this.activeFilters = []
        this.activeSort = 'RELEVANCE'
        this.products = []
        this.gridItems = []
        this.offset = 0
        this.minPriceLimit = 0
        this.minPrice = 0
        this.minPriceLabel = ''
        this.maxPriceLimit = 0
        this.maxPrice = 0
        this.maxPriceLabel = ''
        this.minThumb = 0
        this.maxThumb = 0
        this.buffer = 0
    },
    resetFilters() {
        if (!this.filters.length) return

        this.clearFilters()

        const url = new URL(window.location)
        url.search = ''
        window.history.replaceState({}, '', url)
    },
    createRequest(returnFilters = false, filtersToReturn = [], initialApplyFilters = null) {
        const request = {
            recordQueries: [
                {
                    id: 'categoryProducts',
                    typeOfRequest: 'CATNAV',
                    settings: {
                        query: {
                            categoryPath: this.collectionName,
                        },
                        sort: this.activeSort,
                        limit: this.limit - this.limitReduction(),
                        offset: this.offset || 0,
                        fields: [
                            'id',
                            'itemGroupId',
                            'name',
                            'price',
                            'salePrice',
                            'url',
                            'image',
                            'imageHover',
                            'additionalDataToReturn',
                            'tag',
                        ],
                        typeOfRecords: ['KLEVU_PRODUCT'],
                    },
                },
            ],
            context: {
                apiKeys: [shopifyData.klevuApi.key],
            },
        }

        const record = request.recordQueries[0]

        if (returnFilters) {
            record.filters = {
                filtersToReturn: {
                    enabled: true,
                    options: {
                        limit: 100,
                    },
                },
            }

            if (filtersToReturn.length) {
                record.filters.filtersToReturn.include = filtersToReturn
            }
        }

        // If initial apply-filters are provided (Klevu format), attach them
        if (initialApplyFilters && initialApplyFilters.length) {
            record.filters = record.filters || {}
            record.filters.applyFilters = {
                filters: initialApplyFilters,
            }
        }

        const activeFilters = this.filters.filter((filter) => {
            return filter.options.some((option) => option.selected)
        })

        if (activeFilters.length) {
            const filtersToApply = activeFilters.map((filter) => {
                const values = filter.options
                    .filter((option) => option.selected)
                    .map((option) => option.value)
                return {
                    key: filter.key,
                    values: values,
                    settings: {
                        singleSelect: false,
                    },
                }
            })

            record.filters = record.filters || {}

            record.filters.applyFilters = {
                filters: [...filtersToApply],
            }
        }

        return request
    },
    fetchProducts(request, loadingKey = 'loading', initialFetch = false) {
        this[loadingKey] = true

        fetch(shopifyData.klevuApi.searchUrl, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
            },
            body: JSON.stringify(request),
        })
            .then((response) => response.json())
            .then(async (data) => {
                if (initialFetch) {
                    this.setFilters(data)

                    // If we parsed URL params earlier, match them against the
                    // returned filters and apply selections for matching keys.
                    if (this.urlCandidateParams) {
                        this.applyUrlCandidatesToFilters()
                        this.fetchFilteredSortedProducts()
                        return
                    }
                }

                const products = await convertKlevuProductData(data.queryResults[0].records)

                if (initialFetch || !this.offset) {
                    this.totalProductsFound = data.queryResults[0].meta.totalResultsFound
                    this.products = products
                } else {
                    this.products.push(...products)
                }

                const pageIndex = Math.ceil(this.offset / this.limit)
                const cta = this.ctas[pageIndex] || null

                if (cta) {
                    let startIndex = 4 * (cta.rowPosition - 1)

                    // If the CTA is not full width, allow for the CTA to be
                    // inserted in the correct column position
                    if (cta.colSpan !== 4) {
                        startIndex += cta.colPosition - 1
                    }

                    this.gridItems = [
                        ...this.gridItems,
                        ...products.slice(0, startIndex),
                        cta &&
                        this.gridItems.length +
                            products.length -
                            2 >
                            startIndex
                            ? cta
                            : null,
                        ...products.slice(startIndex),
                    ]
                } else {
                    this.gridItems = [
                        ...this.gridItems,
                        ...products,
                    ]
                }

                this[loadingKey] = false
            })
            .catch((err) => {
                console.error(err)
                this[loadingKey] = false
            })
    },
    fetchFilteredSortedProducts() {
        this.offset = 0
        this.gridItems = []

        const request = this.createRequest()

        this.fetchProducts(request)
    },
    loadMore() {
        this.offset += this.limit - this.limitReduction()

        const request = this.createRequest()
        this.fetchProducts(request, 'loadMoreLoading')
    },
    setFilters(data) {
        this.filters = this.transformFilters(data.queryResults[0].filters)

        const brandFilter = this.filters.find(
            (filter) => filter.key === 'brand'
        )
        if (brandFilter && brandFilter.options.length <= 1) {
            this.filters = this.filters.filter(
                (filter) => filter.key !== 'brand'
            )
        }

        this.sortSizeFilter()

        this.updateActiveFilters()

        const priceFilter = this.filters.find(
            (filter) => filter.key === 'price'
        )
        if (priceFilter && this.currency.code === this.currency.default) {
            this.initPriceFilter(priceFilter)
        } else {
            this.filters = this.filters.filter(
                (filter) => filter.key !== 'price'
            )
        }
    },
    sortSizeFilter() {
        const sizeFilter = this.filters.find((filter) => filter.key === 'size')

        if (sizeFilter) {
            const orderedOptions = []

            const stringOptions = sizeFilter.options.filter(
                (option) => !/\d/.test(option.value)
            )
            stringOptions.sort((a, b) => {
                const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL']
                return sizeOrder.indexOf(a.value) - sizeOrder.indexOf(b.value)
            })
            orderedOptions.push(...stringOptions)

            const numericalOptions = sizeFilter.options.filter((option) =>
                /\d/.test(option.value)
            )
            numericalOptions.sort((a, b) => a.value - b.value)
            orderedOptions.push(...numericalOptions)

            sizeFilter.options = orderedOptions
        }
    },
    initPriceFilter(filter) {
        const minPrice = Number(filter.options[0].value)
        const maxPrice = Number(filter.options[filter.options.length - 1].value)

        this.buffer = Math.ceil(((maxPrice - minPrice) * 0.1 + 1) / 10) * 10

        this.minPriceLimit = minPrice
        this.minPrice = minPrice
        this.maxPriceLimit = maxPrice
        this.maxPrice = maxPrice
        this.minPriceTrigger()
        this.maxPriceTrigger()
    },
    minPriceTrigger() {
        const priceFilter = this.filters.find((filter) => filter.key === 'price')
        if (!priceFilter || !priceFilter.options?.length) return

        let minPrice = Number(this.minPrice)
        let maxPrice = Number(this.maxPrice)

        if (maxPrice - minPrice < this.buffer) {
            minPrice = maxPrice - this.buffer
            this.minPrice = minPrice
        }

        this.minThumb =
            ((minPrice - this.minPriceLimit) * 100) /
            (this.maxPriceLimit - this.minPriceLimit)

        this.minPriceLabel = formatMoney(minPrice * 100)

        this.updatePriceFilter(priceFilter)
    },
    maxPriceTrigger() {
        const priceFilter = this.filters.find((filter) => filter.key === 'price')
        if (!priceFilter || !priceFilter.options?.length) return

        let minPrice = Number(this.minPrice)
        let maxPrice = Number(this.maxPrice)

        if (maxPrice - minPrice < this.buffer) {
            maxPrice = minPrice + this.buffer
            this.maxPrice = maxPrice
        }

        this.maxThumb =
            100 -
            ((maxPrice - this.minPriceLimit) * 100) /
                (this.maxPriceLimit - this.minPriceLimit)

        this.maxPriceLabel = formatMoney(this.maxPrice * 100)

        this.updatePriceFilter(priceFilter)
    },
    updatePriceFilter(priceFilter = null) {
        priceFilter =
            priceFilter || this.filters.find((filter) => filter.key === 'price')

        if (!priceFilter || !priceFilter.options?.length) return

        priceFilter.options.forEach((option) => {
            const value = Number(option.value)
            const minPrice = Number(this.minPrice)
            const maxPrice = Number(this.maxPrice)

            option.selected = value >= minPrice && value <= maxPrice
        })
    },
    updateActiveFilters() {
        const activeFilters = this.filters.filter(
            (filter) =>
                filter.key !== 'tags' &&
                filter.key !== 'price' &&
                filter.options.some((option) => option.selected)
        )

        this.activeFilters = activeFilters.reduce((acc, filter) => {
            const selectedOptions = filter.options
                .filter((option) => option.selected)
                .map((option) => {
                    return {
                        filterKey: filter.key,
                        ...option,
                    }
                })

            return [...acc, ...selectedOptions]
        }, [])
    },
    clearFilter(key, value) {
        const filter = this.filters.find((filter) => {
            return filter.key === key
        })

        if (filter) {
            const option = filter.options.find((option) => option.value === value)
            option.selected = false
        }
    },
    clearFilters() {
        this.filters = this.filters.map((filter) => {
            filter.options = filter.options.map((option) => {
                option.selected = false

                return option
            })

            return filter
        })

        this.minPriceTrigger()
        this.maxPriceTrigger()
    },
    transformFilters(filtersData) {
        const filters = filtersData.map((filter) => {
            if (filter.key === 'price') {
                filter.options.sort((a, b) => a.value - b.value)

                filter.options = filter.options.map((option) => {
                    option.name = formatMoney(option.name)
                    return option
                })
            } else {
                filter.options.sort((a, b) => a.name.localeCompare(b.name))
            }

            return filter
        })

        return filters
    },
    handleProductClick(e, product) {
        sendKlevuAnalytics(e, product, '', true)
    },
    updateSelectWidth(el) {
        const width = this.$refs.activeSortLabel.offsetWidth
        const padding = el.computedStyleMap().get('padding-right').value || 0

        if (!width) return

        el.style.width = width + padding + 'px'
    },
    setActiveSortLabel(value) {
        this.activeSortLabel = this.sortOptions.find(
            (option) => option.value === value
        ).label
    },
    toggleColumnSize() {
        this.columnSize = this.columnSize === 2 ? 1 : 2
    },
    limitReduction() {
        // This is used to offset the number of products to fetch so that each
        // page has the correct number of products
        const pageIndex = Math.ceil(this.offset / this.limit)
        const cta = this.ctas[pageIndex] || null

        return cta ? cta.colSpan : 0
    },
})
